/**
 * Marka Bazlı Canlı Takip. Sayfa markadan bağımsız; hangi markanın panelinde
 * açıldığına göre varsayılan ürün markası değişir (High5 → ASICS,
 * HUPALUPA Store → NeeDoh) ve seçiciden diğerlerine geçilir.
 *
 * İki uçtan besleniyor:
 *   /api/brand-live/summary  → günlük ciro/sipariş/adet/çeşit + Google, Meta ve
 *                              TikTok harcaması
 *   /api/brand-live/live     → bugün (ve dün) için CANLI ciro + harcama;
 *                              60 sn'de bir tazelenir
 *
 * Canlı satırlar summary'nin üzerine yazılır: raw harcama tabloları dünde bittiği
 * için bugünün harcaması yalnızca canlı uçtan gelir.
 *
 * 🔑 Cironun kaynağı markaya göre değişiyor ve uç bunu `source.revenue` ile
 * söylüyor: BigQuery export'u olan markada ham olaylar, olmayanda (HUPALUPA)
 * GA4 Data API. İkisinin yanıt şekli aynı, farkı yalnız kapsam — kaynağı
 * çözülemeyen ciro dilimi Data API'de daha büyük, sayfa bunu yazıyor.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  RefreshCw, Radio, Wallet, TrendingUp, ShoppingCart, Package,
  Boxes, AlertTriangle, Info, Download, Clock, Eye, Users, PlayCircle, Image as ImageIcon,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

/* Doğrulanmış kategorik palet (dataviz slot 1-3, açık tema).
 * Ciro aqua'nın yüzeye kontrastı 3:1'in altında → tablo görünümü + doğrudan
 * etiket zorunlu; ikisi de bu sayfada var. */
const C_REVENUE = '#1baf7a';
const C_GOOGLE = '#2a78d6';
const C_META = '#eb6834';
/* TikTok yığının 3. segmenti. Aday renkler validate_palette.js ile ölçüldü
 * (--pairs all, light): violet TÜM kontrolleri geçiyor (en kötü CVD ΔE 13,0,
 * kontrast ≥3:1); sarı normal görüşte 13,7 ile, kırmızı CVD'de 5,6 ile KALDI —
 * ikisi de turuncuya fazla yakın. Aqua geçiyor ama o renk bu sayfada "ciro"
 * demek, yığında ikinci bir anlam yüklenemez. */
const C_TIKTOK = '#4a3aa7';
const C_ROAS = '#4a3aa7';

/* Panel markası → sayfa açılırken seçili gelecek ürün markası. Backend'deki
 * DEFAULT_ITEM_BRAND ile aynı liste; ikisi birlikte değişmeli. Listede olmayan
 * marka için liste yüklendikten sonra ciroca en büyüğe düşülüyor. */
const DEFAULT_BRAND_BY_PANEL = {
  'b20e13f1-2e3f-4c24-83ce-f2b812bfc3e8': 'ASICS',   // High5
  'cb6bbe2d-a3cd-4322-a4e4-2373d10225a9': 'NeeDoh',  // HUPALUPA Store
};
const defaultBrandFor = (id) => DEFAULT_BRAND_BY_PANEL[id] || 'ASICS';

const CREATIVE_TABS = [['META', 'Meta'], ['TIKTOK', 'TikTok'], ['GOOGLE_ADS', 'Google']];

const nf = (n, d = 0) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
const money = (n) => `₺${nf(n)}`;
const roasFmt = (n) => (n == null ? '—' : `${nf(n, 2)}×`);
// Eksen etiketi: 140000 → "140B₺". Yalnız "140B" milyar diye okunabiliyordu.
const binFmt = (v) => (Math.abs(v) >= 1000 ? `${nf(v / 1000)}B₺` : `${nf(v)}₺`);

/* ── Kanal kimlikleri ────────────────────────────────────────────────────────
 * Renk kanalın KENDİSİNE bağlı, sıraya değil: liste ciroya göre sıralansa da
 * Google Ads hep mavi, Meta hep turuncu kalır. Slot sırası doğrulanmış
 * kategorik paletten (bitişik çiftlerde CVD ΔE ≥ 8) alındı.
 * Kanal barları TEK renkte çiziliyor — oradaki iş büyüklük, kimlik değil;
 * kimliği satır etiketi taşıyor. Bu noktalar yalnızca tablo ile grafiği
 * birbirine bağlayan işaretler.
 */
const CHANNELS = {
  google_ads:        { label: 'Google Ads',          color: '#2a78d6' },
  meta_ads:          { label: 'Meta Ads',            color: '#eb6834' },
  tiktok_ads:        { label: 'TikTok Ads',          color: '#4a3aa7' },
  organic:           { label: 'Organik arama',       color: '#1baf7a' },
  email:             { label: 'E-posta',             color: '#eda100' },
  social:            { label: 'Sosyal (organik)',    color: '#e87ba4' },
  referral:          { label: 'Yönlendirme',         color: '#008300' },
  /* 🔑 "Doğrudan" gri, çünkü bir mecra kimliği değil ARTIK DİLİM — kaynağı
   * çözülemeyen ciro buraya düşüyor ve kapsam bandındaki "Kaynak yok" ile aynı
   * şeyi anlatıyor. Violet'i bıraktı: o renk artık TikTok Ads'in ve ikisi aynı
   * tabloda yan yana çıkıyor. */
  direct:            { label: 'Doğrudan',            color: '#9ca3af' },
  programmatic:      { label: 'Programatik (RTB)',   color: '#e34948' },
  crm:               { label: 'SMS / CRM',           color: '#6b7280' },
  ai:                { label: 'AI asistan',          color: '#6b7280' },
  other:             { label: 'Diğer',               color: '#9ca3af' },
  unassigned_notset: { label: 'Kaynak (not set)',    color: '#eda100' },
  unassigned_none:   { label: 'Kaynak yok',          color: '#9ca3af' },
};
const chMeta = (k) => CHANNELS[k] || { label: k || '—', color: '#9ca3af' };
const UNASSIGNED = new Set(['unassigned_none', 'unassigned_notset']);

/* Kapsam bandı: kaynağın hangi ölçümden çözüldüğü — GA4 oturumu / AEM / hiçbiri.
 * Kimlik ailesi değil, ölçümün üç hali; her segmentin kendi etiketi var. */
const COV_SESSION = '#1baf7a';
const COV_AEM = '#2a78d6';
const COV_NONE = '#9ca3af';

const GUN_ADI = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const dayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const dayName = (iso) => GUN_ADI[new Date(`${iso}T00:00:00Z`).getUTCDay()];

const RANGES = [14, 30, 45, 90];

function Stat({ icon: Icon, label, value, sub, tone = 'slate', big = false }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-600',
    green: 'bg-emerald-100 text-emerald-700',
    blue: 'bg-blue-100 text-blue-700',
    orange: 'bg-orange-100 text-orange-700',
    violet: 'bg-violet-100 text-violet-700',
  };
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-2">
        <span className={`w-7 h-7 rounded-lg grid place-items-center ${tones[tone]}`}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      </div>
      <div className={`mt-1 font-bold text-gray-900 tabular-nums ${big ? 'text-3xl' : 'text-2xl'}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label, suffix }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg text-xs">
      <div className="font-semibold text-gray-900 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-gray-700">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
          <span className="flex-1">{p.name}</span>
          <span className="font-semibold tabular-nums">
            {suffix === 'roas' ? roasFmt(p.value) : money(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function BrandLiveTracker() {
  const { selectedBrand } = useAuth();

  const [brandName, setBrandName] = useState(() => defaultBrandFor(selectedBrand?.id));
  const [brandOptions, setBrandOptions] = useState([]);
  const [days, setDays] = useState(45);
  // Serbest aralık: ikisi de doluysa hızlı gün butonlarının yerine geçer.
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const isCustom = Boolean(customStart && customEnd);

  const [summary, setSummary] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [liveLoading, setLiveLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [campaignTab, setCampaignTab] = useState('ALL');
  const [creatives, setCreatives] = useState(null);
  const [creativesLoading, setCreativesLoading] = useState(false);
  const [creativeTab, setCreativeTab] = useState('META');

  const liveTimer = useRef(null);
  /* İstek sırası sayaçları: marka ya da aralık hızlı değişince önceki isteğin
   * yanıtı sonra dönüp yenisini eziyordu (iki tarih alanını arka arkaya
   * doldurmak bunu her seferinde tetikliyor). Yalnızca en son isteğin yanıtı
   * ekrana yazılır. */
  const summarySeq = useRef(0);
  const liveSeq = useRef(0);
  const creativeSeq = useRef(0);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'X-Brand-ID': selectedBrand?.id || '',
  }), [selectedBrand?.id]);

  const call = useCallback(async (path) => {
    const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', headers });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d?.error || `HTTP ${res.status}`);
    }
    return res.json();
  }, [headers]);

  /* Marka listesi. Panel markası değişince seçili ürün markası da sıfırlanıyor:
   * ASICS High5'in markası, HUPALUPA panelinde açık kalırsa sayfa baştan sona
   * sıfır gösterir ve "veri yok" sanılır. Liste geldikten sonra seçim hâlâ
   * listede değilse ciroca en büyüğe düşülüyor — varsayılan haritası bir markayı
   * unutsa bile sayfa dolu açılır. */
  useEffect(() => {
    if (!selectedBrand?.id) return;
    setBrandName(defaultBrandFor(selectedBrand.id));
    call('/api/brand-live/brands')
      .then((d) => {
        const list = d.brands || [];
        setBrandOptions(list);
        setBrandName((cur) => {
          if (!list.length) return cur;
          return list.some((b) => b.label.toLowerCase() === String(cur).toLowerCase())
            ? cur : list[0].label;
        });
      })
      .catch(() => setBrandOptions([]));
  }, [selectedBrand?.id, call]);

  const loadSummary = useCallback(async (force = false) => {
    if (!selectedBrand?.id) return;
    const seq = ++summarySeq.current;
    setLoading(true); setError('');
    try {
      const qs = isCustom
        ? `start=${customStart}&end=${customEnd}`
        : `days=${days}`;
      const data = await call(`/api/brand-live/summary?brand=${encodeURIComponent(brandName)}&${qs}${force ? '&refresh=1' : ''}`);
      if (seq !== summarySeq.current) return;
      setSummary(data);
    } catch (e) {
      if (seq !== summarySeq.current) return;
      setSummary(null); setError(e.message || 'Yüklenemedi');
    } finally {
      if (seq === summarySeq.current) setLoading(false);
    }
  }, [selectedBrand?.id, brandName, days, isCustom, customStart, customEnd, call]);

  const loadLive = useCallback(async (force = false) => {
    if (!selectedBrand?.id) return;
    const seq = ++liveSeq.current;
    setLiveLoading(true);
    try {
      const data = await call(`/api/brand-live/live?brand=${encodeURIComponent(brandName)}&back=2${force ? '&refresh=1' : ''}`);
      if (seq === liveSeq.current) setLive(data);
    } catch {
      /* canlı uç düşerse sayfa summary ile ayakta kalsın */
    } finally {
      if (seq === liveSeq.current) setLiveLoading(false);
    }
  }, [selectedBrand?.id, brandName, call]);

  /* Reklam görselleri ayrı uçtan ve TEMBEL: canlı Meta + TikTok çağrıları
   * saniyeler sürüyor, sayfanın ilk açılışını bekletmemeleri gerekiyor.
   * 60 sn'lik canlı döngüye de girmiyor — kreatifler dakika dakika değişmez. */
  const loadCreatives = useCallback(async (force = false) => {
    if (!selectedBrand?.id) return;
    const seq = ++creativeSeq.current;
    setCreativesLoading(true);
    try {
      const qs = isCustom && customStart && customEnd
        ? `start=${customStart}&end=${customEnd}` : `days=${days}`;
      const data = await call(`/api/brand-live/creatives?brand=${encodeURIComponent(brandName)}&${qs}${force ? '&refresh=1' : ''}`);
      if (seq === creativeSeq.current) setCreatives(data);
    } catch {
      if (seq === creativeSeq.current) setCreatives(null);
    } finally {
      if (seq === creativeSeq.current) setCreativesLoading(false);
    }
  }, [selectedBrand?.id, brandName, days, isCustom, customStart, customEnd, call]);

  useEffect(() => { loadSummary(false); loadLive(false); }, [loadSummary, loadLive]);
  useEffect(() => { loadCreatives(false); }, [loadCreatives]);

  // 60 sn'lik canlı tazeleme — sunucu tarafı cache de 60 sn, boşa API çağrısı olmuyor.
  useEffect(() => {
    if (liveTimer.current) clearInterval(liveTimer.current);
    if (!autoRefresh) return undefined;
    liveTimer.current = setInterval(() => loadLive(true), 60_000);
    return () => { if (liveTimer.current) clearInterval(liveTimer.current); };
  }, [autoRefresh, loadLive]);

  /* ── Canlı satırları geçmişin üzerine yaz ─────────────────────────────────
   * Bugün ve dün için harcamanın tek doğru kaynağı canlı uç: raw tablolar
   * dünde bitiyor, bugünü hiç görmüyor. */
  const rows = useMemo(() => {
    const base = (summary?.rows || []).map((r) => ({ ...r }));
    const byDate = new Map(base.map((r) => [r.date, r]));
    for (const lr of (live?.rows || [])) {
      const t = byDate.get(lr.date);
      if (!t) continue;
      t.revenue = lr.revenue; t.orders = lr.orders; t.qty = lr.qty; t.products = lr.products;
      t.googleSpend = lr.googleSpend; t.metaSpend = lr.metaSpend;
      t.tiktokSpend = lr.tiktokSpend || 0; t.spend = lr.spend;
      t.impressions = lr.impressions; t.clicks = lr.clicks;
      t.roas = lr.roas; t.spendSource = 'live';
    }
    return base;
  }, [summary, live]);

  /* "Bugün" seçili pencerenin SONU değil, gerçek bugün — serbest aralıkta bitiş
   * geçmişte olabiliyor. Pencere bugünü kapsamıyorsa BUGÜN kartı doğrudan canlı
   * uçtan besleniyor; sayfanın canlı yanı aralık seçiminden bağımsız. */
  const today = summary?.range?.today || live?.range?.endDate;
  const todayRow = rows.find((r) => r.date === today) || live?.today || null;

  const totals = useMemo(() => {
    const t = rows.reduce((a, r) => ({
      revenue: a.revenue + r.revenue, orders: a.orders + r.orders, qty: a.qty + r.qty,
      googleSpend: a.googleSpend + r.googleSpend, metaSpend: a.metaSpend + r.metaSpend,
      tiktokSpend: a.tiktokSpend + (r.tiktokSpend || 0),
      spend: a.spend + r.spend, impressions: a.impressions + (r.impressions || 0),
      clicks: a.clicks + (r.clicks || 0),
    }), { revenue: 0, orders: 0, qty: 0, googleSpend: 0, metaSpend: 0, tiktokSpend: 0, spend: 0, impressions: 0, clicks: 0 });
    t.roas = t.spend > 0 ? t.revenue / t.spend : null;
    // Bugün yarım; ortalama tam günlerden alınır yoksa hep aşağı çeker.
    const full = rows.filter((r) => r.date !== today);
    t.avgRevenue = full.length ? full.reduce((s, r) => s + r.revenue, 0) / full.length : 0;
    t.avgSpend = full.length ? full.reduce((s, r) => s + r.spend, 0) / full.length : 0;
    return t;
  }, [rows, today]);

  // Başlıklarda "45 gün" ya da serbest aralıkta "01.07 – 31.07 (31 gün)".
  const periodLabel = useMemo(() => {
    const r = summary?.range;
    if (!r) return `${days} gün`;
    if (r.endDate === r.today) return `${r.days} gün`;
    return `${dayLabel(r.startDate)}–${dayLabel(r.endDate)} (${r.days}g)`;
  }, [summary, days]);

  /* TikTok yalnızca gerçekten harcama varsa gösteriliyor: hesabı olmayan
   * markada (High5) baştan sona sıfır dolu bir kolon/segment tabloyu ve yığını
   * boş yere kalabalıklaştırır. */
  const hasTiktok = useMemo(
    () => rows.some((r) => (r.tiktokSpend || 0) > 0) || (live?.today?.tiktokSpend || 0) > 0,
    [rows, live],
  );

  const chartData = useMemo(() => rows.map((r) => ({
    gun: dayLabel(r.date),
    date: r.date,
    Ciro: Math.round(r.revenue),
    Google: Math.round(r.googleSpend),
    Meta: Math.round(r.metaSpend),
    ...(hasTiktok ? { TikTok: Math.round(r.tiktokSpend || 0) } : {}),
    ROAS: r.roas == null ? null : Number(r.roas.toFixed(2)),
  })), [rows, hasTiktok]);

  /* ── Kampanya × gün pivotu ───────────────────────────────────────────────
   * Geçmiş summary'den, bugün/dün canlı uçtan. Aynı (kampanya, gün) canlıdan
   * gelirse DB'nin üzerine yazılır. */
  const campaigns = useMemo(() => {
    const cells = new Map(); // `${platform}|${name}` -> { platform, name, byDate }
    const put = (platform, name, date, cost, impressions, clicks, overwrite) => {
      const k = `${platform}|${name}`;
      if (!cells.has(k)) cells.set(k, { platform, name, byDate: new Map(), total: 0 });
      const c = cells.get(k);
      const prev = c.byDate.get(date);
      if (prev && !overwrite) return;
      c.byDate.set(date, { cost, impressions, clicks });
    };
    for (const r of (summary?.campaignDaily || [])) {
      put(r.platform, r.campaignName, r.date, r.cost, r.impressions, r.clicks, false);
    }
    for (const r of (live?.campaigns || [])) {
      put(r.platform, r.campaignName, r.date, r.cost, r.impressions, r.clicks, true);
    }
    const dates = rows.map((r) => r.date);
    const out = [...cells.values()].map((c) => {
      const total = dates.reduce((s, d) => s + (c.byDate.get(d)?.cost || 0), 0);
      return { ...c, total };
    }).filter((c) => c.total > 0);
    out.sort((a, b) => b.total - a.total);
    return out;
  }, [summary, live, rows]);

  const visibleCampaigns = campaignTab === 'ALL'
    ? campaigns : campaigns.filter((c) => c.platform === campaignTab);

  /* ── Ciro hangi kaynaktan geldi ─────────────────────────────────────────────
   * Kanal payları "kaynağı belli olan ciro" üzerinden okunur; kaynağa
   * bağlanamayan iki dilim (sunucu taraflı sipariş + (not set)) paydadan
   * çıkarılıp ayrı kapsam bandında gösteriliyor. Toplam ciroya bölseydik her
   * kanal olduğundan küçük görünürdü. */
  const sources = summary?.sources || null;
  const coverage = sources?.coverage || null;

  /* Payların paydası uçtan geliyor: BigQuery yolunda "kaynağı çözülen ciro"
   * (çözülemeyen dilim kanal listesinde yok), Data API yolunda toplam ciro
   * ("doğrudan" hem listede hem çözülemeyen sayılıyor — eski payda ile paylar
   * %130'a çıkıyordu). */
  const shareBase = coverage?.shareBase ?? coverage?.assigned ?? 0;
  const shareBaseIsTotal = coverage != null && shareBase === coverage.total;

  const channelRows = useMemo(() => {
    const list = (sources?.channels || []).filter((c) => !UNASSIGNED.has(c.channel) && c.revenue > 0);
    const base = shareBase || list.reduce((a, c) => a + c.revenue, 0);
    return list.map((c) => ({ ...c, share: base > 0 ? c.revenue / base : 0 }));
  }, [sources, shareBase]);

  /* Kampanya adı iki tarafta da aynı yazılıyor (GG/CO/SEARCHBR/BRAND …), o yüzden
   * GA4 cirosunu harcamayla ada göre eşleştirebiliyoruz. Eşleşmeyen satırın
   * ROAS'ı boş kalır — harcama tarafı yalnız adında marka geçen kampanyaları
   * getirdiği için markaya satış yapan başka kampanyalar burada harcamasız görünür. */
  const spendByCampaign = useMemo(() => {
    const m = new Map();
    for (const c of campaigns) {
      const k = String(c.name || '').trim().toLowerCase();
      if (k) m.set(k, (m.get(k) || 0) + c.total);
    }
    return m;
  }, [campaigns]);

  const detailRows = useMemo(() => (sources?.detail || [])
    .filter((d) => d.revenue > 0 && !UNASSIGNED.has(d.channel))
    .map((d) => {
      const spend = d.campaign ? (spendByCampaign.get(d.campaign.trim().toLowerCase()) ?? null) : null;
      return { ...d, spend, roas: spend > 0 ? d.revenue / spend : null };
    }), [sources, spendByCampaign]);

  // Kapsam bandı segmentleri — çubuk ve açıklama aynı listeden okusun.
  /* Sıfır dilim çizilmiyor: BigQuery export'u olmayan markada AEM ölçümü hiç
   * yok ve "AEM ₺0" satırı bandı okunmaz kılıyordu. */
  const covSegments = useMemo(() => (coverage ? [
    ['GA4 oturumundan', coverage.bySession, COV_SESSION],
    ['AEM ölçümünden (çerez izni yok)', coverage.byAem, COV_AEM],
    ['Event UTM\'inden', coverage.byCollected, COV_AEM],
    ['Kaynak yok', coverage.unknown, COV_NONE],
  ].filter(([, v]) => Number(v || 0) > 0) : []), [coverage]);

  // Bugünün kırılımı canlı uçtan; /live bunu yalnız bugün için hesaplıyor.
  const todayChannels = useMemo(() => {
    const list = (live?.sources?.channels || []).filter((c) => c.revenue > 0);
    const total = list.reduce((a, c) => a + c.revenue, 0);
    return list.map((c) => ({ ...c, share: total > 0 ? c.revenue / total : 0 }));
  }, [live]);

  // Dönem geneli en çok satanlar summary'den; canlı uç yalnızca bugünü/dünü
  // kapsadığı için onun ürün listesi burada kullanılmıyor.
  const topProducts = summary?.topProducts || [];

  // Uç ciroyu nereden okumuş — dipnotlar buna göre değişiyor.
  const bqMode = !String(summary?.source?.revenue || '').includes('Data API');
  /* GA4 Data API ürün kimliğini ve ürün başına sipariş sayısını vermiyor
   * (ikincisi kapsam çakışması). Boş kolon göstermek yerine kolonu kaldırıyoruz. */
  const hasItemId = topProducts.some((p) => p.itemId);
  const hasItemOrders = topProducts.some((p) => p.orders != null);
  // Aynı sebep kaynak kırılımında da geçerli: kaynak × kampanya kırılımında
  // sipariş sayısı GA4 Data API'sinden alınamıyor, kolon sıfır dolu kalıyordu.
  const hasDetailOrders = detailRows.some((d) => (d.orders || 0) > 0);
  const hasChannelOrders = channelRows.some((c) => (c.orders || 0) > 0);

  /* ── Erişim & görünürlük ─────────────────────────────────────────────────
   * Satırların rengi mecra kimliğiyle aynı (Google mavi, Meta turuncu, TikTok
   * violet) — sayfadaki harcama yığınıyla aynı sözlük. */
  const awareness = summary?.awareness || null;
  const awarenessRows = useMemo(() => {
    if (!awareness) return [];
    return [
      { key: 'google', label: 'Google Ads', color: C_GOOGLE, ...awareness.google },
      { key: 'meta', label: 'Meta Ads', color: C_META, ...awareness.meta },
      { key: 'tiktok', label: 'TikTok Ads', color: C_TIKTOK, ...awareness.tiktok },
    ].filter((r) => r.impressions > 0 || r.spend > 0);
  }, [awareness]);

  const awarenessTotals = useMemo(() => {
    const t = awarenessRows.reduce((a, r) => ({
      impressions: a.impressions + (r.impressions || 0),
      clicks: a.clicks + (r.clicks || 0),
      videoViews: a.videoViews + (r.videoViews || 0),
      spend: a.spend + (r.spend || 0),
    }), { impressions: 0, clicks: 0, videoViews: 0, spend: 0 });
    t.ctr = t.impressions > 0 ? t.clicks / t.impressions : 0;
    t.cpm = t.impressions > 0 ? (t.spend / t.impressions) * 1000 : null;
    return t;
  }, [awarenessRows]);

  /* 🔑 "Toplam erişim" diye bir kart YOK — mecralar toplanamaz. Onun yerine
   * en geniş erişimi olan mecra gösteriliyor: bu sayı gerçek ve tek bir
   * mecranın tekilleştirilmiş rakamı. */
  /* ── Reklam içerikleri ───────────────────────────────────────────────── */
  const creativeCounts = useMemo(() => ({
    META: creatives?.meta?.ads?.length || 0,
    TIKTOK: creatives?.tiktok?.ads?.length || 0,
    GOOGLE_ADS: creatives?.google?.assets?.length || 0,
  }), [creatives]);

  const visibleCreatives = useMemo(() => {
    if (creativeTab === 'META') return creatives?.meta?.ads || [];
    if (creativeTab === 'TIKTOK') return creatives?.tiktok?.ads || [];
    return [];
  }, [creatives, creativeTab]);

  /* Google'ın GÖRSEL varlıkları: PMax asset'leri ve YouTube videoları.
   * Metin varlıklarından ayrı duruyorlar çünkü biri kart, öbürü liste. */
  const googleVisuals = useMemo(
    () => (creatives?.google?.assets || []).filter((a) => a.image),
    [creatives],
  );

  /* Google METİN varlıkları kampanya kampanya toplanıyor; aynı başlık birden çok
   * reklam grubunda geçtiği için tekilleştiriliyor, yoksa liste tekrarla dolar. */
  const googleByCampaign = useMemo(() => {
    const map = new Map();
    for (const a of (creatives?.google?.assets || [])) {
      if (!a.text || a.image) continue;
      const key = a.campaignName || '—';
      if (!map.has(key)) map.set(key, { campaignName: key, headlines: new Set(), descriptions: new Set() });
      const c = map.get(key);
      if (a.fieldType === 'DESCRIPTION') c.descriptions.add(a.text);
      else c.headlines.add(a.text);
    }
    return [...map.values()]
      .map((c) => ({ ...c, headlines: [...c.headlines], descriptions: [...c.descriptions] }))
      .sort((a, b) => (b.headlines.length + b.descriptions.length) - (a.headlines.length + a.descriptions.length));
  }, [creatives]);

  const creativeErrors = useMemo(() => {
    const e = creatives?.errors || {};
    return [
      e.meta && `Meta — ${e.meta}`,
      e.tiktok && `TikTok — ${e.tiktok}`,
      e.google && `Google — ${e.google}`,
    ].filter(Boolean);
  }, [creatives]);

  const widestReach = useMemo(() => {
    const withReach = awarenessRows.filter((r) => r.reach != null && r.reach > 0);
    if (!withReach.length) return null;
    return withReach.reduce((a, b) => (b.reach > a.reach ? b : a));
  }, [awarenessRows]);

  // Tablolarda tarihler yeniden eskiye; her hücre için yeniden çevirmemek için bir kez.
  const rowsDesc = useMemo(() => [...rows].reverse(), [rows]);

  const csv = () => {
    const head = ['Tarih', 'Gün', 'Ciro', 'Sipariş', 'Adet', 'Ürün çeşidi', 'Google', 'Meta',
      ...(hasTiktok ? ['TikTok'] : []), 'Toplam harcama', 'ROAS'];
    const lines = rows.map((r) => [
      r.date, dayName(r.date), r.revenue.toFixed(2), r.orders, r.qty, r.products,
      r.googleSpend.toFixed(2), r.metaSpend.toFixed(2),
      ...(hasTiktok ? [Number(r.tiktokSpend || 0).toFixed(2)] : []),
      r.spend.toFixed(2),
      r.roas == null ? '' : r.roas.toFixed(2),
    ].join(';'));
    const blob = new Blob([['sep=;', head.join(';'), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${brandName}_gunluk_${summary?.range?.startDate}_${summary?.range?.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const csvSources = () => {
    const head = ['Kanal', 'Kaynak', 'Mecra', 'Kampanya', 'Sipariş', 'Adet', 'Ciro', 'Pay(%)', 'Harcama', 'ROAS'];
    const base = coverage?.shareBase ?? coverage?.assigned ?? 0;
    const lines = detailRows.map((d) => [
      chMeta(d.channel).label, d.source || '', d.medium || '', d.campaign || '',
      d.orders, d.qty, d.revenue.toFixed(2),
      base > 0 ? ((d.revenue / base) * 100).toFixed(2) : '',
      d.spend == null ? '' : d.spend.toFixed(2),
      d.roas == null ? '' : d.roas.toFixed(2),
    ].join(';'));
    // Kaynağa bağlanamayan iki dilim dosyanın sonunda ayrı satır olarak durur —
    // toplamın CSV'de de kapanması için.
    if (coverage) {
      const cov = [
        ['GA4 oturumundan çözülen', coverage.bySession],
        ['AEM ölçümünden çözülen (çerez izni yok)', coverage.byAem],
        ['Event UTM\'inden çözülen', coverage.byCollected],
        ['Kaynağı çözülemeyen', coverage.unknown],
        ['TOPLAM ciro', coverage.total],
      ];
      for (const [label, v] of cov) {
        lines.push([label, '', '', '', '', '', Number(v || 0).toFixed(2), '', '', ''].join(';'));
      }
    }
    const blob = new Blob([['sep=;', head.join(';'), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${brandName}_ciro_kaynak_${summary?.range?.startDate}_${summary?.range?.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const lastUpdate = live?.generatedAt ? new Date(live.generatedAt) : null;

  return (
    <div className="p-4 md:p-6 space-y-4 bg-gray-50 min-h-screen">
      {/* Başlık + kontroller */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            {brandName} — Canlı Takip
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Günlük ciro, ürün ve harcama · bugün canlı
            {lastUpdate && <> · son güncelleme {lastUpdate.toLocaleTimeString('tr-TR')}</>}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white"
          >
            {!brandOptions.some((b) => b.label === brandName) && <option value={brandName}>{brandName}</option>}
            {brandOptions.map((b) => <option key={b.key} value={b.label}>{b.label}</option>)}
          </select>

          <div className="flex rounded-lg border border-gray-300 overflow-hidden bg-white">
            {RANGES.map((d) => (
              <button
                key={d}
                onClick={() => { setDays(d); setCustomStart(''); setCustomEnd(''); }}
                className={`px-2.5 py-1.5 text-sm ${!isCustom && days === d ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >{d}g</button>
            ))}
          </div>

          {/* Serbest aralık — iki alan da dolunca hızlı butonların yerine geçer */}
          <div className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 bg-white ${
            isCustom ? 'border-gray-900' : 'border-gray-300'}`}>
            <input
              type="date"
              value={customStart}
              max={customEnd || summary?.range?.today || undefined}
              onChange={(e) => setCustomStart(e.target.value)}
              className="text-xs text-gray-700 bg-transparent outline-none w-[112px]"
              aria-label="Başlangıç tarihi"
            />
            <span className="text-gray-300 text-xs">→</span>
            <input
              type="date"
              value={customEnd}
              min={customStart || undefined}
              max={summary?.range?.today || undefined}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="text-xs text-gray-700 bg-transparent outline-none w-[112px]"
              aria-label="Bitiş tarihi"
            />
            {(customStart || customEnd) && (
              <button
                onClick={() => { setCustomStart(''); setCustomEnd(''); }}
                className="text-gray-400 hover:text-gray-700 text-sm leading-none px-1"
                title="Aralığı temizle"
              >×</button>
            )}
          </div>

          <label className="flex items-center gap-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-lg px-2.5 py-1.5">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            60 sn'de yenile
          </label>

          <button
            onClick={() => { loadSummary(true); loadLive(true); }}
            disabled={loading}
            className="flex items-center gap-1.5 text-sm bg-gray-900 text-white rounded-lg px-3 py-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading || liveLoading ? 'animate-spin' : ''}`} />
            Yenile
          </button>

          <button onClick={csv} disabled={!rows.length}
            className="flex items-center gap-1.5 text-sm border border-gray-300 bg-white rounded-lg px-3 py-1.5 disabled:opacity-50">
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* BUGÜN — canlı */}
      <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3">
        <div className="flex items-center gap-2 mb-2">
          <Radio className={`w-4 h-4 text-emerald-600 ${liveLoading ? 'animate-pulse' : ''}`} />
          <span className="text-sm font-bold text-emerald-900">
            BUGÜN — {today ? `${dayLabel(today)} ${dayName(today)}` : '—'}
          </span>
          <span className="text-[11px] text-emerald-700">
            ciro GA4'ten · harcama reklam hesaplarından canlı
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          <Stat icon={TrendingUp} tone="green" big label="Ciro" value={money(todayRow?.revenue)} />
          <Stat icon={Wallet} tone="slate" big label="Harcama" value={money(todayRow?.spend)}
            sub={`Google ${money(todayRow?.googleSpend)} · Meta ${money(todayRow?.metaSpend)}`
              + (hasTiktok ? ` · TikTok ${money(todayRow?.tiktokSpend)}` : '')} />
          <Stat icon={TrendingUp} tone="violet" big label="ROAS" value={roasFmt(todayRow?.roas)} />
          <Stat icon={ShoppingCart} tone="blue" label="Sipariş" value={nf(todayRow?.orders)} />
          <Stat icon={Boxes} tone="blue" label="Satılan adet" value={nf(todayRow?.qty)} />
          <Stat icon={Package} tone="orange" label="Ürün çeşidi" value={nf(todayRow?.products)} />
          <Stat icon={Clock} tone="slate" label="Tık / Gösterim"
            value={nf(todayRow?.clicks)} sub={`${nf(todayRow?.impressions)} gösterim`} />
        </div>
        {/* Bugünün cirosu hangi kanaldan — canlı uç bunu yalnız bugün için hesaplıyor */}
        {todayChannels.length > 0 && (
          <div className="mt-2 pt-2 border-t border-emerald-200/70">
            <div className="text-[11px] font-semibold text-emerald-900 mb-1">Bugünün cirosu nereden geldi</div>
            <div className="flex flex-wrap gap-1.5">
              {todayChannels.map((c) => (
                <span key={c.channel}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-2 py-1 text-[11px]">
                  <span className="w-2 h-2 rounded-sm" style={{ background: chMeta(c.channel).color }} />
                  <span className="text-gray-600">{chMeta(c.channel).label}</span>
                  <b className="text-gray-900 tabular-nums">{money(c.revenue)}</b>
                  <span className="text-gray-400 tabular-nums">%{nf(c.share * 100, 0)}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {(live?.errors?.google || live?.errors?.meta || live?.errors?.tiktok) && (
          <div className="mt-2 text-[11px] text-amber-700 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Canlı harcama eksik:
            {live.errors.google && <span> Google — {live.errors.google}.</span>}
            {live.errors.meta && <span> Meta — {live.errors.meta}.</span>}
            {live.errors.tiktok && <span> TikTok — {live.errors.tiktok}.</span>}
          </div>
        )}
      </div>

      {/* Dönem özeti */}
      <div className={`grid grid-cols-2 md:grid-cols-3 gap-2 ${hasTiktok ? 'xl:grid-cols-7' : 'xl:grid-cols-6'}`}>
        <Stat icon={TrendingUp} tone="green" label={`Ciro · ${periodLabel}`} value={money(totals.revenue)}
          sub={`gün ort. ${money(totals.avgRevenue)}`} />
        <Stat icon={Wallet} tone="slate" label="Toplam harcama" value={money(totals.spend)}
          sub={`gün ort. ${money(totals.avgSpend)}`} />
        <Stat icon={TrendingUp} tone="violet" label="ROAS" value={roasFmt(totals.roas)}
          sub="marka adlı kampanyalara göre" />
        <Stat icon={Wallet} tone="blue" label="Google" value={money(totals.googleSpend)} />
        <Stat icon={Wallet} tone="orange" label="Meta" value={money(totals.metaSpend)} />
        {hasTiktok && <Stat icon={Wallet} tone="violet" label="TikTok" value={money(totals.tiktokSpend)} />}
        <Stat icon={ShoppingCart} tone="blue" label="Sipariş / Adet"
          value={`${nf(totals.orders)} / ${nf(totals.qty)}`} />
      </div>

      {/* ── Erişim & görünürlük ────────────────────────────────────────────
        * 🔑🔑 Erişim satırı MECRA MECRA duruyor ve TOPLAMI YOK — bilerek.
        * Aynı kişi hem Meta'da hem TikTok'ta görmüş olabilir; üçünü toplamak
        * "şu kadar kişiye ulaştık" diye okunur ve yanlıştır. Gösterim, tık ve
        * izlenme olay sayısı olduğu için toplanabilir, onların toplamı var. */}
      {awareness && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200 flex flex-wrap items-center gap-2 justify-between">
            <span className="text-sm font-semibold text-gray-900">
              Erişim &amp; görünürlük — {periodLabel}
            </span>
            <span className="text-[11px] text-gray-400">
              gösterim/tık/izlenme senkron tablolardan · erişim mecra API'sinden tekilleştirilmiş
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 border-b border-gray-100">
            <Stat icon={Eye} tone="slate" big label="Toplam gösterim" value={nf(awarenessTotals.impressions)}
              sub={`${nf(awarenessTotals.clicks)} tık · CTR %${nf(awarenessTotals.ctr * 100, 2)}`} />
            <Stat icon={PlayCircle} tone="violet" big label="Video izlenme" value={nf(awarenessTotals.videoViews)}
              sub="Google + Meta + TikTok" />
            <Stat icon={Wallet} tone="slate" label="Ortalama CPM"
              value={awarenessTotals.cpm == null ? '—' : `₺${nf(awarenessTotals.cpm, 2)}`}
              sub="bin gösterim maliyeti" />
            <Stat icon={Users} tone="blue" label="En geniş erişim"
              value={widestReach ? nf(widestReach.reach) : '—'}
              sub={widestReach ? `${widestReach.label} · tekil kişi` : 'erişim verisi yok'} />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="text-left px-3 py-2 font-semibold">Mecra</th>
                  <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                  <th className="text-right px-3 py-2 font-semibold">Erişim</th>
                  <th className="text-right px-3 py-2 font-semibold">Frekans</th>
                  <th className="text-right px-3 py-2 font-semibold">Video izlenme</th>
                  <th className="text-right px-3 py-2 font-semibold">Tık</th>
                  <th className="text-right px-3 py-2 font-semibold">CTR</th>
                  <th className="text-right px-3 py-2 font-semibold">CPM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {awarenessRows.map((r) => (
                  <tr key={r.key} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <span className="inline-block w-2 h-2 rounded-sm mr-2 align-middle" style={{ background: r.color }} />
                      <span className="font-medium text-gray-800">{r.label}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900">{nf(r.impressions)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                      {r.reach == null
                        ? <span className="text-gray-300" title={r.reachNote || ''}>—</span>
                        : nf(r.reach)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                      {r.frequency == null ? <span className="text-gray-300">—</span> : `${nf(r.frequency, 2)}×`}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{nf(r.videoViews)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{nf(r.clicks)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                      {r.ctr == null ? '—' : `%${nf(r.ctr * 100, 2)}`}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                      {r.cpm == null ? '—' : `₺${nf(r.cpm, 2)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr className="font-bold text-gray-900">
                  <td className="px-3 py-2">Toplam</td>
                  <td className="px-3 py-2 text-right tabular-nums">{nf(awarenessTotals.impressions)}</td>
                  {/* 🔑 Erişimin toplamı BİLEREK yok — bkz. bölüm başındaki not. */}
                  <td className="px-3 py-2 text-right text-[11px] font-normal text-gray-400">toplanamaz</td>
                  <td className="px-3 py-2 text-right text-gray-300">—</td>
                  <td className="px-3 py-2 text-right tabular-nums">{nf(awarenessTotals.videoViews)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{nf(awarenessTotals.clicks)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">%{nf(awarenessTotals.ctr * 100, 2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {awarenessTotals.cpm == null ? '—' : `₺${nf(awarenessTotals.cpm, 2)}`}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="px-3 py-2 border-t border-gray-100 text-[11px] text-gray-500 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-px shrink-0" />
            <span>
              <b>Erişim tekil kişi sayısı, gösterim ise gösterilme sayısı.</b> Erişim ne günler
              arasında ne de mecralar arasında toplanır — aynı kişi birden çok gün ve birden çok
              mecrada sayılır. Bu yüzden her satır kendi mecrasının dönem geneli için
              tekilleştirilmiş sayısını gösteriyor, alttaki toplam satırında erişim yok.
              {' '}Google Ads bu uçta erişim vermiyor (arama ve PMax'te tekil kullanıcı metriği yok).
            </span>
          </div>
        </div>
      )}

      {/* Grafikler — ciro ile harcama arasında ~15× büyüklük farkı var; aynı
       * eksende harcama çubukları kayboluyordu. Çift eksen yasak olduğu için
       * üçü de kendi grafiğinde, ortak zaman ekseniyle yan yana duruyor. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-sm font-semibold text-gray-900 mb-2">Günlük ciro (₺)</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" vertical={false} />
              <XAxis dataKey="gun" tick={{ fontSize: 10, fill: '#6b7280' }} interval="preserveStartEnd"
                axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false}
                tickFormatter={binFmt} width={44} />
              <RTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
              <Bar dataKey="Ciro" fill={C_REVENUE} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-sm font-semibold text-gray-900 mb-2">Günlük harcama (₺)</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" vertical={false} />
              <XAxis dataKey="gun" tick={{ fontSize: 10, fill: '#6b7280' }} interval="preserveStartEnd"
                axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false}
                tickFormatter={binFmt} width={44} />
              <RTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
              <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
              {/* Yığında segmentler arasında 1px yüzey boşluğu — bitişik renkler kaynaşmasın */}
              <Bar dataKey="Google" stackId="s" fill={C_GOOGLE} stroke="#fff" strokeWidth={1} />
              <Bar dataKey="Meta" stackId="s" fill={C_META} stroke="#fff" strokeWidth={1}
                radius={hasTiktok ? undefined : [4, 4, 0, 0]} />
              {hasTiktok && (
                <Bar dataKey="TikTok" stackId="s" fill={C_TIKTOK} stroke="#fff" strokeWidth={1} radius={[4, 4, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-sm font-semibold text-gray-900 mb-2">Günlük ROAS (×)</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" vertical={false} />
              <XAxis dataKey="gun" tick={{ fontSize: 10, fill: '#6b7280' }} interval="preserveStartEnd"
                axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false} width={30} />
              <RTooltip content={<ChartTooltip suffix="roas" />} />
              <Line type="monotone" dataKey="ROAS" stroke={C_ROAS} strokeWidth={2}
                dot={{ r: 2.5, fill: C_ROAS }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Günlük tablo */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900">Günlük döküm</span>
          <span className="text-[11px] text-gray-400">
            {summary?.range?.startDate} → {summary?.range?.endDate}
          </span>
        </div>
        <div className="overflow-x-auto max-h-[520px]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-[11px] uppercase tracking-wide text-gray-500">
                <th className="text-left px-3 py-2 font-semibold">Tarih</th>
                <th className="text-right px-3 py-2 font-semibold">Ciro</th>
                <th className="text-right px-3 py-2 font-semibold">Sipariş</th>
                <th className="text-right px-3 py-2 font-semibold">Adet</th>
                <th className="text-right px-3 py-2 font-semibold">Çeşit</th>
                <th className="text-right px-3 py-2 font-semibold">Google</th>
                <th className="text-right px-3 py-2 font-semibold">Meta</th>
                {hasTiktok && <th className="text-right px-3 py-2 font-semibold">TikTok</th>}
                <th className="text-right px-3 py-2 font-semibold">Harcama</th>
                <th className="text-right px-3 py-2 font-semibold">ROAS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rowsDesc.map((r) => {
                const isToday = r.date === today;
                const gecici = !r.finalized && !isToday;
                return (
                  <tr key={r.date} className={isToday ? 'bg-emerald-50/60' : 'hover:bg-gray-50'}>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{dayLabel(r.date)}</span>
                      <span className="text-gray-400 ml-1.5 text-xs">{dayName(r.date)}</span>
                      {isToday && <span className="ml-1.5 text-[10px] font-semibold text-emerald-700">CANLI</span>}
                      {gecici && <span className="ml-1.5 text-[10px] text-amber-600">geçici</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900">{money(r.revenue)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{nf(r.orders)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{nf(r.qty)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{nf(r.products)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{money(r.googleSpend)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{money(r.metaSpend)}</td>
                    {hasTiktok && <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{money(r.tiktokSpend)}</td>}
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium text-gray-800">{money(r.spend)}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${
                      r.roas == null ? 'text-gray-300' : r.roas >= 8 ? 'text-emerald-700' : r.roas >= 4 ? 'text-gray-800' : 'text-red-600'
                    }`}>{roasFmt(r.roas)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 sticky bottom-0">
              <tr className="font-bold text-gray-900">
                <td className="px-3 py-2">Toplam</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(totals.revenue)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{nf(totals.orders)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{nf(totals.qty)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-400">—</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(totals.googleSpend)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(totals.metaSpend)}</td>
                {hasTiktok && <td className="px-3 py-2 text-right tabular-nums">{money(totals.tiktokSpend)}</td>}
                <td className="px-3 py-2 text-right tabular-nums">{money(totals.spend)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{roasFmt(totals.roas)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Ciro nereden geldi ──────────────────────────────────────────────
       * Kanal barları tek renkte: buradaki iş büyüklük, kimliği satır etiketi
       * taşıyor. Renkli nokta yalnızca aşağıdaki detay tablosuyla bağ kuruyor. */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 flex flex-wrap items-center gap-2 justify-between">
          <span className="text-sm font-semibold text-gray-900">
            Ciro nereden geldi — {periodLabel}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-400">
              {bqMode ? 'GA4 oturum son tıklaması + AEM kaynak parametresi'
                : 'GA4 oturum son tıklaması (kampanya adından mecra düzeltmesiyle)'}
            </span>
            <button onClick={csvSources} disabled={!detailRows.length}
              className="flex items-center gap-1 text-xs border border-gray-300 bg-white rounded-lg px-2 py-1 disabled:opacity-40">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        </div>

        {!coverage && (
          <div className="px-3 py-6 text-center text-sm text-gray-400">
            {sources?.error ? `Kaynak kırılımı alınamadı: ${sources.error}` : 'Kaynak kırılımı yükleniyor…'}
          </div>
        )}

        {coverage && (
          <>
            {/* Kapsam — payların paydası burada belirleniyor */}
            <div className="p-3 border-b border-gray-100">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1.5">
                <span className="text-xs font-semibold text-gray-700">
                  Cironun ne kadarı bir kaynağa bağlanabiliyor?
                </span>
                <span className="text-[11px] text-gray-500 tabular-nums">
                  toplam {money(coverage.total)}
                </span>
              </div>
              <div className="flex h-7 w-full rounded-lg overflow-hidden bg-gray-100 gap-[2px]">
                {covSegments.filter(([, v]) => v > 0).map(([label, v, color]) => (
                  <div key={label} title={`${label}: ${money(v)}`}
                    style={{ width: `${(v / (coverage.total || 1)) * 100}%`, background: color }} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[11px] text-gray-600">
                {covSegments.map(([label, v, color]) => (
                  <span key={label} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
                    {label}
                    <b className="text-gray-900 tabular-nums">{money(v)}</b>
                    <span className="text-gray-400 tabular-nums">
                      %{nf((v / (coverage.total || 1)) * 100, 1)}
                    </span>
                  </span>
                ))}
              </div>
              {coverage.byAem > 0 && (
                <p className="mt-2 text-[11px] text-gray-500 flex items-start gap-1.5">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <span>
                    Ziyaretçilerin bir bölümü <b>çerez izni vermiyor</b>
                    (<code className="px-1 bg-gray-100 rounded">analytics_storage: No</code>) —
                    GA4 o siparişte oturum kimliği de kaynak da yazmıyor. Bu satırlar sitedeki
                    <b> AEM ölçümünün</b> kaynak parametresinden çözülüyor, o yüzden aşağıdaki
                    paylar cironun tamamını kapsıyor.{' '}
                    <b>Kampanya adı</b> ise yalnız GA4 oturumu olan{' '}
                    {money(coverage.campaignScope)} ciroda var — AEM kampanya taşımıyor.
                  </span>
                </p>
              )}
              {coverage.unknown > 0 && (
                <p className="mt-1.5 text-[11px] text-amber-700 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <span>
                    {money(coverage.unknown)} ciroda iki ölçüm de kaynak yazmamış; bu dilim
                    aşağıdaki payların dışında.
                  </span>
                </p>
              )}
            </div>

            {coverage.note && (
              <div className="mx-3 mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1.5 text-[11px] text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                <span>{coverage.note}</span>
              </div>
            )}

            {/* Kanal payları — payda: kaynağı belli olan ciro */}
            <div className="p-3 space-y-1.5">
              <div className="text-[11px] text-gray-500 mb-1">
                {shareBaseIsTotal
                  ? <>Paylar <b>toplam ciro {money(coverage.total)}</b> üzerinden
                    {coverage.unknown > 0
                      ? ' — kaynağı çözülemeyen dilim de kanal olarak listede duruyor.'
                      : '; cironun tamamının kaynağı çözülmüş.'}</>
                  : <>Paylar <b>kaynağı çözülen {money(coverage.assigned)}</b> üzerinden
                    {coverage.unknown > 0 ? ' (kaynağı çözülemeyen ciro hariç)' : ''}.</>}
              </div>
              {channelRows.map((c) => (
                <div key={c.channel} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ background: chMeta(c.channel).color }} />
                  <span className="text-xs text-gray-700 w-32 md:w-40 shrink-0 truncate">
                    {chMeta(c.channel).label}
                  </span>
                  <div className="flex-1 h-4 bg-gray-100 rounded-r-md overflow-hidden min-w-[40px]">
                    <div className="h-full rounded-r-md"
                      style={{ width: `${Math.max(c.share * 100, 0.5)}%`, background: C_REVENUE }} />
                  </div>
                  <span className="text-xs tabular-nums font-semibold text-gray-900 w-24 text-right shrink-0">
                    {money(c.revenue)}
                  </span>
                  <span className="text-[11px] tabular-nums text-gray-500 w-12 text-right shrink-0">
                    %{nf(c.share * 100, 1)}
                  </span>
                  {hasChannelOrders && (
                    <span className="hidden sm:inline text-[11px] tabular-nums text-gray-400 w-16 text-right shrink-0">
                      {nf(c.orders)} sip.
                    </span>
                  )}
                </div>
              ))}
              {!channelRows.length && (
                <div className="py-6 text-center text-sm text-gray-400">
                  Bu pencerede kaynağı belli olan ciro yok.
                </div>
              )}
            </div>

            {/* Kaynak / kampanya kırılımı — kanal barlarının tablo görünümü */}
            <div className="border-t border-gray-100">
              <div className="px-3 py-2 text-xs font-semibold text-gray-700">
                Kaynak · kampanya kırılımı
              </div>
              <div className="overflow-x-auto max-h-[420px]">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-[11px] uppercase tracking-wide text-gray-500">
                      <th className="text-left px-3 py-2 font-semibold">Kanal</th>
                      <th className="text-left px-3 py-2 font-semibold">Kaynak / mecra</th>
                      <th className="text-left px-3 py-2 font-semibold">Kampanya</th>
                      {hasDetailOrders && <th className="text-right px-3 py-2 font-semibold">Sipariş</th>}
                      <th className="text-right px-3 py-2 font-semibold">Ciro</th>
                      <th className="text-right px-3 py-2 font-semibold">Pay</th>
                      <th className="text-right px-3 py-2 font-semibold">Harcama</th>
                      <th className="text-right px-3 py-2 font-semibold">ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {detailRows.map((d, i) => (
                      <tr key={`${d.channel}|${d.source}|${d.medium}|${d.campaign}|${i}`} className="hover:bg-gray-50">
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <span className="inline-block w-2 h-2 rounded-sm mr-2 align-middle"
                            style={{ background: chMeta(d.channel).color }} />
                          <span className="text-gray-700 text-xs">{chMeta(d.channel).label}</span>
                        </td>
                        <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">
                          {d.source || '—'}{d.medium ? ` / ${d.medium}` : ''}
                        </td>
                        <td className="px-3 py-1.5 text-xs font-medium text-gray-800">
                          {d.campaign || <span className="text-gray-300">—</span>}
                        </td>
                        {hasDetailOrders && <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{nf(d.orders)}</td>}
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900">{money(d.revenue)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                          %{nf((d.revenue / (shareBase || 1)) * 100, 1)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                          {d.spend == null ? <span className="text-gray-300">—</span> : money(d.spend)}
                        </td>
                        <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${
                          d.roas == null ? 'text-gray-300' : d.roas >= 8 ? 'text-emerald-700' : d.roas >= 4 ? 'text-gray-800' : 'text-red-600'
                        }`}>{roasFmt(d.roas)}</td>
                      </tr>
                    ))}
                    {!detailRows.length && (
                      <tr><td colSpan={hasDetailOrders ? 8 : 7} className="px-3 py-6 text-center text-gray-400">Kayıt yok.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 text-[11px] text-gray-500 border-t border-gray-100">
                Harcama sütunu, GA4'ün kampanya adını harcama tablosundaki kampanya adıyla
                eşleştirerek doluyor. <b>Boş harcama</b>, o kampanyanın adında “{brandName}”
                geçmediği için harcama tarafına hiç girmediği anlamına gelir — ciroyu getiriyor
                ama maliyeti bu sayfada görünmüyor.{' '}
                <b className="text-amber-700">Buradaki ROAS aşağı sapar:</b> ciro yalnızca
                “{brandName}” markalı ürünleri sayıyor, harcama ise kampanyanın tamamını —
                aynı kampanya başka marka da satmışsa o ciro bu satıra girmiyor.
              </div>
            </div>
          </>
        )}
      </div>

      {/* Kampanyalar × gün */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 flex flex-wrap items-center gap-2 justify-between">
          <span className="text-sm font-semibold text-gray-900">
            Kampanyalar × gün — harcama (₺)
          </span>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            {[['ALL', 'Hepsi'], ['GOOGLE_ADS', 'Google'], ['META', 'Meta'],
              ...(hasTiktok ? [['TIKTOK', 'TikTok']] : [])].map(([k, l]) => (
              <button key={k} onClick={() => setCampaignTab(k)}
                className={`px-2.5 py-1 text-xs ${campaignTab === k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto max-h-[420px]">
          <table className="text-xs">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-[10px] uppercase tracking-wide text-gray-500">
                <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-gray-50 z-20 min-w-[240px]">Kampanya</th>
                <th className="text-right px-2 py-2 font-semibold">Toplam</th>
                {rowsDesc.map((r) => (
                  <th key={r.date} className={`text-right px-2 py-2 font-semibold whitespace-nowrap ${
                    r.date === today ? 'text-emerald-700' : ''}`}>{dayLabel(r.date)}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleCampaigns.map((c) => (
                <tr key={`${c.platform}|${c.name}`} className="hover:bg-gray-50">
                  <td className="px-3 py-1.5 sticky left-0 bg-white z-10 whitespace-nowrap">
                    <span className="inline-block w-2 h-2 rounded-sm mr-2"
                      style={{ background: c.platform === 'GOOGLE_ADS' ? C_GOOGLE
                        : c.platform === 'TIKTOK' ? C_TIKTOK : C_META }} />
                    <span className="font-medium text-gray-800">{c.name}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-gray-900">{money(c.total)}</td>
                  {rowsDesc.map((r) => {
                    const cell = c.byDate.get(r.date);
                    return (
                      <td key={r.date} className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap ${
                        cell ? 'text-gray-700' : 'text-gray-200'} ${r.date === today ? 'bg-emerald-50/60' : ''}`}>
                        {cell ? nf(cell.cost) : '·'}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!visibleCampaigns.length && (
                <tr><td colSpan={rows.length + 2} className="px-3 py-6 text-center text-gray-400">
                  Adında “{brandName}” geçen kampanya bulunamadı.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Reklam görselleri ve metinleri ──────────────────────────────────
        * 🔑 Üç mecranın "reklam içeriği" aynı şey değil: Meta ve TikTok'ta bir
        * görsel/video + metin var, Google aramada ise reklam METİN parçalarından
        * (başlık/açıklama) kuruluyor ve gösterildiği hâli sabit değil. Bu yüzden
        * Google ayrı bir düzende, kart değil metin listesi olarak duruyor. */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 flex flex-wrap items-center gap-2 justify-between">
          <span className="text-sm font-semibold text-gray-900">
            Reklam içerikleri — {periodLabel}
          </span>
          <div className="flex items-center gap-2">
            {creativesLoading && <span className="text-[11px] text-gray-400">yükleniyor…</span>}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              {CREATIVE_TABS.map(([k, l]) => {
                const cnt = creativeCounts[k] || 0;
                return (
                  <button key={k} onClick={() => setCreativeTab(k)}
                    className={`px-2.5 py-1 text-xs ${creativeTab === k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                    {l} <span className={creativeTab === k ? 'text-gray-300' : 'text-gray-400'}>{cnt}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={() => loadCreatives(true)} disabled={creativesLoading}
              className="flex items-center gap-1 text-xs border border-gray-300 bg-white rounded-lg px-2 py-1 disabled:opacity-40">
              <RefreshCw className={`w-3.5 h-3.5 ${creativesLoading ? 'animate-spin' : ''}`} /> Yenile
            </button>
          </div>
        </div>

        {creativeErrors.length > 0 && (
          <div className="px-3 py-2 border-b border-gray-100 text-[11px] text-amber-700 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
            <span>{creativeErrors.join(' · ')}</span>
          </div>
        )}

        {/* Meta ve TikTok: görselli kart ızgarası */}
        {creativeTab !== 'GOOGLE_ADS' && (
          <div className="p-3">
            {!visibleCreatives.length ? (
              <div className="py-8 text-center text-sm text-gray-400">
                {creativesLoading ? 'Reklamlar getiriliyor…'
                  : `Bu pencerede adında “${brandName}” geçen ${creativeTab === 'META' ? 'Meta' : 'TikTok'} reklamı bulunamadı.`}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                {visibleCreatives.map((a) => (
                  <div key={`${a.platform}|${a.adId}`}
                    className="rounded-xl border border-gray-200 overflow-hidden flex flex-col bg-white">
                    <div className="relative bg-gray-100 aspect-[4/5] flex items-center justify-center overflow-hidden">
                      {(a.imageProxy || a.image) ? (
                        // Kreatifler dikey (9:16) ya da kare olabiliyor; kutu 4:5 ve
                        // görsel `contain` — `cover` olsaydı story kreatifinin üstü
                        // ve altı kırpılır, metin/logo kaybolurdu.
                        // 🔑 Vekil üzerinden yükleniyor (imzalı CDN linkleri 24-48
                        // saatte ölüyor, vekil diske alıyor); vekil düşerse özgün
                        // URL'e bir kez geri düşülüyor.
                        <img
                          src={a.imageProxy ? `${API_BASE}${a.imageProxy}` : a.image}
                          alt={a.adName || 'reklam'}
                          loading="lazy"
                          onError={(e) => {
                            if (a.image && e.currentTarget.src !== a.image) e.currentTarget.src = a.image;
                          }}
                          className="w-full h-full object-contain" />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-gray-300" />
                      )}
                      <div className="absolute top-1.5 left-1.5 flex gap-1">
                        {a.isVideo && (
                          <span className="inline-flex items-center gap-0.5 rounded bg-black/65 text-white text-[10px] px-1.5 py-0.5">
                            <PlayCircle className="w-3 h-3" /> video
                          </span>
                        )}
                        {a.isSpark && (
                          <span className="rounded bg-black/65 text-white text-[10px] px-1.5 py-0.5">Spark</span>
                        )}
                        {a.isCatalog && (
                          <span className="rounded bg-black/65 text-white text-[10px] px-1.5 py-0.5"
                            title="Katalog (DPA) reklamı — görsel yayın anında ürün feed'inden seçiliyor; burada feed'den örnek ürünler gösteriliyor">
                            katalog
                          </span>
                        )}
                      </div>
                      {a.status && !/ACTIVE|ENABLE/i.test(a.status) && (
                        <span className="absolute top-1.5 right-1.5 rounded bg-amber-500/90 text-white text-[10px] px-1.5 py-0.5">
                          {a.status.toLowerCase()}
                        </span>
                      )}
                    </div>
                    <div className="p-2 flex-1 flex flex-col gap-1">
                      {a.headline && (
                        <div className="text-[12px] font-semibold text-gray-900 leading-tight line-clamp-2">{a.headline}</div>
                      )}
                      {a.body && (
                        <div className="text-[11px] text-gray-600 leading-snug line-clamp-4 whitespace-pre-line">{a.body}</div>
                      )}
                      {/* Katalog reklamında tek bir görsel yok; feed'den örnek ürünler. */}
                      {a.productImages?.length > 1 && (
                        <div className="flex gap-1">
                          {a.productImages.slice(1, 4).map((src, i) => (
                            <img key={i} src={src} alt="" loading="lazy"
                              className="w-8 h-8 rounded border border-gray-200 object-cover bg-gray-50" />
                          ))}
                          <span className="text-[10px] text-gray-400 self-center">feed'den</span>
                        </div>
                      )}
                      <div className="mt-auto pt-1.5 border-t border-gray-100 text-[11px] text-gray-500 space-y-0.5">
                        <div className="flex justify-between tabular-nums">
                          <span>Harcama</span><b className="text-gray-800">{money(a.spend)}</b>
                        </div>
                        <div className="flex justify-between tabular-nums">
                          <span>Gösterim</span><span>{nf(a.impressions)}</span>
                        </div>
                        <div className="flex justify-between tabular-nums">
                          <span>Tık / CTR</span>
                          <span>{nf(a.clicks)}{a.impressions > 0 ? ` · %${nf((a.clicks / a.impressions) * 100, 2)}` : ''}</span>
                        </div>
                        <div className="text-gray-400 truncate" title={a.campaignName || ''}>{a.campaignName || '—'}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Google: metin varlıkları, kampanya kampanya */}
        {creativeTab === 'GOOGLE_ADS' && (
          <div className="p-3 space-y-3">
            {!googleByCampaign.length && !googleVisuals.length && (
              <div className="py-8 text-center text-sm text-gray-400">
                {creativesLoading ? 'Varlıklar getiriliyor…'
                  : `Bu pencerede adında “${brandName}” geçen Google varlığı bulunamadı.`}
              </div>
            )}

            {/* PMax görselleri + YouTube videoları — Meta/TikTok'takiyle aynı ızgara */}
            {googleVisuals.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                {googleVisuals.map((a, i) => (
                  <div key={`${a.campaignName}|${a.youtubeId || a.image}|${i}`}
                    className="rounded-xl border border-gray-200 overflow-hidden flex flex-col bg-white">
                    <div className="relative bg-gray-100 aspect-[4/5] flex items-center justify-center overflow-hidden">
                      <img
                        src={a.imageProxy ? `${API_BASE}${a.imageProxy}` : a.image}
                        alt={a.text || 'varlık'} loading="lazy"
                        onError={(e) => {
                          if (a.image && e.currentTarget.src !== a.image) e.currentTarget.src = a.image;
                        }}
                        className="w-full h-full object-contain" />
                      <div className="absolute top-1.5 left-1.5 flex gap-1">
                        {a.youtubeId && (
                          <span className="inline-flex items-center gap-0.5 rounded bg-black/65 text-white text-[10px] px-1.5 py-0.5">
                            <PlayCircle className="w-3 h-3" /> YouTube
                          </span>
                        )}
                        {a.source === 'pmax' && (
                          <span className="rounded bg-black/65 text-white text-[10px] px-1.5 py-0.5">PMax</span>
                        )}
                      </div>
                      {a.width && a.height && (
                        <span className="absolute bottom-1.5 right-1.5 rounded bg-black/55 text-white text-[10px] px-1.5 py-0.5">
                          {a.width}×{a.height}
                        </span>
                      )}
                    </div>
                    <div className="p-2 flex-1 flex flex-col gap-1">
                      {(a.youtubeTitle || a.text) && (
                        <div className="text-[12px] font-semibold text-gray-900 leading-tight line-clamp-2">
                          {a.youtubeTitle || a.text}
                        </div>
                      )}
                      <div className="text-[10px] uppercase tracking-wide text-gray-400">
                        {String(a.fieldType || '').replace(/_/g, ' ').toLowerCase()}
                      </div>
                      <div className="mt-auto pt-1.5 border-t border-gray-100 text-[11px] text-gray-500 space-y-0.5">
                        {a.spend != null ? (
                          <>
                            <div className="flex justify-between tabular-nums">
                              <span>Harcama</span><b className="text-gray-800">{money(a.spend)}</b>
                            </div>
                            <div className="flex justify-between tabular-nums">
                              <span>Gösterim</span><span>{nf(a.impressions)}</span>
                            </div>
                            <div className="flex justify-between tabular-nums">
                              <span>İzlenme</span><span>{nf(a.videoViews)}</span>
                            </div>
                          </>
                        ) : (
                          // 🔴 PMax'te asset başına metrik YOK — sayı uydurmak yerine
                          // neden olmadığı yazılıyor.
                          <div className="text-gray-400 text-[10px] leading-snug">
                            Google PMax'te varlık başına metrik vermiyor
                          </div>
                        )}
                        <div className="text-gray-400 truncate" title={a.campaignName || ''}>
                          {a.assetGroupName ? `${a.campaignName} · ${a.assetGroupName}` : (a.campaignName || '—')}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {googleByCampaign.map((c) => (
              <div key={c.campaignName} className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-800">
                  {c.campaignName}
                  <span className="ml-2 font-normal text-gray-400">
                    {c.headlines.length} başlık · {c.descriptions.length} açıklama
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 p-3">
                  <div>
                    <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Başlıklar</div>
                    <ul className="space-y-0.5">
                      {c.headlines.map((t, i) => (
                        <li key={i} className="text-[12px] text-gray-800 leading-snug">• {t}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-3 md:mt-0">
                    <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Açıklamalar</div>
                    <ul className="space-y-0.5">
                      {c.descriptions.map((t, i) => (
                        <li key={i} className="text-[12px] text-gray-600 leading-snug">• {t}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
            <div className="text-[11px] text-gray-500 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-px shrink-0" />
              <span>
                <b>Görseller ve videolar canlı Google Ads API'sinden</b> geliyor —
                PMax varlık ağacı ve <code className="mx-1 px-1 bg-gray-100 rounded">video</code>
                kaynağı (PMax Asset Performance ve Video sayfalarının kullandığı yol).
                Google arama reklamının görseli yok: reklam, başlık ve açıklama
                parçalarından her aramada yeniden kuruluyor, o yüzden aşağıda parçalar
                listeleniyor. Google <b>PMax varlığı başına metrik vermiyor</b>, bu yüzden
                o kartlarda harcama/gösterim yok; YouTube videolarında var.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* En çok satan ürünler */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 text-sm font-semibold text-gray-900">
          En çok satan {brandName} ürünleri — {periodLabel}
        </div>
        <div className="overflow-x-auto max-h-[380px]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-[11px] uppercase tracking-wide text-gray-500">
                <th className="text-left px-3 py-2 font-semibold">#</th>
                <th className="text-left px-3 py-2 font-semibold">Ürün</th>
                {hasItemId && <th className="text-left px-3 py-2 font-semibold">Kod</th>}
                {hasItemOrders && <th className="text-right px-3 py-2 font-semibold">Sipariş</th>}
                <th className="text-right px-3 py-2 font-semibold">Adet</th>
                <th className="text-right px-3 py-2 font-semibold">Ciro</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {topProducts.map((p, i) => (
                <tr key={p.itemId || `${p.itemName}|${i}`} className="hover:bg-gray-50">
                  <td className="px-3 py-1.5 text-gray-400 tabular-nums">{i + 1}</td>
                  <td className="px-3 py-1.5 font-medium text-gray-900">{p.itemName || '—'}</td>
                  {hasItemId && <td className="px-3 py-1.5 text-gray-500 font-mono text-xs">{p.itemId}</td>}
                  {hasItemOrders && <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{nf(p.orders)}</td>}
                  <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{nf(p.qty)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900">{money(p.revenue)}</td>
                </tr>
              ))}
              {!topProducts.length && (
                <tr><td colSpan={4 + (hasItemId ? 1 : 0) + (hasItemOrders ? 1 : 0)}
                  className="px-3 py-6 text-center text-gray-400">Kayıt yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Okuma notları — sayıların ne olduğu ve ne OLMADIĞI */}
      <div className="rounded-xl border border-gray-200 bg-white p-3 text-xs text-gray-600 space-y-1.5">
        <div className="flex items-center gap-1.5 font-semibold text-gray-900">
          <Info className="w-3.5 h-3.5" /> Bu sayfadaki sayılar nasıl okunur
        </div>
        <p>
          {bqMode ? (
            <><b>Ciro</b> doğrudan GA4 BigQuery export'undan geliyor: kapanmış günler
              <code className="mx-1 px-1 bg-gray-100 rounded">events_*</code>, kapanmamış günler
              <code className="mx-1 px-1 bg-gray-100 rounded">events_intraday_*</code>.</>
          ) : (
            <><b>Ciro</b> GA4 Data API'sinden geliyor — bu markanın BigQuery export'u yok.
              Sipariş sayısı ile ürün cirosu GA4'te aynı raporda istenemediği için ayrı
              sorgulardan birleştiriliyor.</>
          )} KDV dahil site cirosu, iade düşülmemiş — ERP net satış tablosuyla (KDV hariç)
          birebir tutmaz.
          {summary?.source?.finalizedThrough && (bqMode
            ? <> GA4 export'u <b>{summary.source.finalizedThrough}</b> tarihine kadar kapanmış;
                sonraki günler “geçici”, gün ilerledikçe büyür.</>
            : <> GA4 verisi <b>{summary.source.finalizedThrough}</b> tarihine kadar oturmuş
                sayılıyor; işleme 48 saate kadar sürebildiği için sonraki günler “geçici”.</>)}
        </p>
        <p>
          <b>Harcama</b> geçmiş günlerde senkron tablolardan
          {summary?.source?.spendGoogleThrough && <> (Google {summary.source.spendGoogleThrough},
            Meta {summary.source.spendMetaThrough} tarihine kadar)</>}, bugün ve dün doğrudan
          Google Ads ile Meta API'lerinden canlı.
          {hasTiktok && (summary?.source?.spendTiktokSource === 'api'
            ? <> <b>TikTok</b>'un tamamı — geçmiş dahil — TikTok API'sinden çekiliyor;
                senkron tablosu bu hesapta tek gün taşıdığı için kaynak olarak kullanılmıyor.</>
            : <> <b>TikTok</b> senkron tablosundan okunuyor.</>)}
        </p>
        <p>
          <b>Kaynak</b> iki ölçümden çözülüyor. Önce oturumun <b>son tıklaması</b>
          (<code className="mx-1 px-1 bg-gray-100 rounded">session_traffic_source_last_click</code>) —
          GA4 arayüzündeki “Oturum varsayılan kanal grubu” ile aynı alan; kampanya adını ve
          Google Ads bağını yalnız bu taşır.
          {bqMode
            ? <> Çerez izni vermeyen ziyaretçide GA4 bu alanı hiç yazmadığı için sitedeki
                <b> AEM ölçümünün</b> kaynak parametresi devreye giriyor.</>
            : <> Mecra ayrıca kampanya adından düzeltiliyor: GA4 bu hesapta Meta trafiğini
                <code className="mx-1 px-1 bg-gray-100 rounded">social/paid</code> diye yazıyor,
                kampanya adı ise hangi reklam hesabına ait olduğunu kesin söylüyor.</>}
          Bir sipariş tek bir kanala yazılır; ilk temas ya da yardımcı kanallar burada
          görünmez, yani bu tablo <b>atıf modeli değil, son tık dökümü</b>.
          {coverage && coverage.assignedShare != null && (
            <> Bu pencerede cironun <b>%{nf(coverage.assignedShare * 100, 1)}</b>'i bir kaynağa
              bağlanabiliyor{coverage.byAem > 0
                ? <>: GA4 oturumu {money(coverage.bySession)}, çerez izni olmayan trafikte
                  AEM ölçümü {money(coverage.byAem)}</>
                : <>; kalan {money(coverage.unknown)} “doğrudan” görünüyor</>}.</>
          )}
        </p>
        <p className="text-amber-700">
          <b>ROAS'ı dikkatli oku:</b> harcamaya yalnızca <b>adında “{brandName}” geçen</b> kampanyalar giriyor
          (GG/CO/PMAX/{brandName.toUpperCase()}, FB/CO/DPA/{brandName.toUpperCase()} …). SMARTSHOPPING,
          SEARCHBR/BRAND gibi markaya satış getiren ama adında marka geçmeyen kampanyalar bu toplamın
          dışında — yani buradaki ROAS “marka kampanyalarının ROAS'ı”, markanın gerçek toplam ROAS'ı değil.
        </p>
      </div>
    </div>
  );
}
